import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { MapPin, ExternalLink, Play, StopCircle, Route as RouteIcon, RefreshCw } from "lucide-react";
import { TrazoGPS, HistorialRutas } from "@/components/TrazoGPS";

function todayIso() {
  return new Date().toLocaleDateString("en-CA", { timeZone: "America/Guatemala" });
}

// Distancia entre 2 puntos (haversine, km)
function haversine(a, b) {
  const R = 6371;
  const toRad = (x) => (x * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

export default function RutaMapa() {
  const [fecha, setFecha] = useState(todayIso());
  const [pilotoId, setPilotoId] = useState("");
  const [repartidores, setRepartidores] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/domicilios/repartidores").then(({ data }) => {
      setRepartidores(data || []);
      if ((data || []).length > 0 && !pilotoId) setPilotoId(data[0].id);
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    if (!pilotoId) return;
    setLoading(true);
    try {
      const { data } = await api.get("/domicilios/ruta", { params: { fecha, piloto_id: pilotoId } });
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [fecha, pilotoId]);
  useEffect(() => {
    const t = setInterval(() => { if (!document.hidden && data?.ruta?.iniciada_at && !data?.ruta?.finalizada_at) load(); }, 30000);
    return () => clearInterval(t);
  });

  const kms = useMemo(() => {
    const pings = data?.ruta?.pings || [];
    let d = 0;
    for (let i = 1; i < pings.length; i++) d += haversine(pings[i - 1], pings[i]);
    return d;
  }, [data]);

  const pings = data?.ruta?.pings || [];
  const inicio = pings[0];
  const fin = pings.length > 1 ? pings[pings.length - 1] : null;
  // URL de Google Maps con los puntos como waypoints (máx 10 waypoints)
  const mapUrl = useMemo(() => {
    if (!inicio) return null;
    if (!fin || (inicio.lat === fin.lat && inicio.lng === fin.lng)) {
      // solo inicio: pin
      return `https://www.google.com/maps/search/?api=1&query=${inicio.lat},${inicio.lng}`;
    }
    // camino: directions con hasta 10 waypoints intermedios
    const middle = pings.slice(1, -1);
    const step = Math.max(1, Math.floor(middle.length / 8));
    const waypoints = [];
    for (let i = 0; i < middle.length; i += step) waypoints.push(`${middle[i].lat},${middle[i].lng}`);
    let url = `https://www.google.com/maps/dir/?api=1&origin=${inicio.lat},${inicio.lng}&destination=${fin.lat},${fin.lng}&travelmode=driving`;
    if (waypoints.length > 0) url += `&waypoints=${waypoints.slice(0, 8).join("|")}`;
    return url;
  }, [pings, inicio, fin]);

  return (
    <div className="space-y-4" data-testid="ruta-mapa-page">
      <div>
        <h1 className="text-3xl font-black uppercase tracking-tight">Mapa Ruta del Día</h1>
        <p className="text-sm text-zinc-500">Consulta el recorrido GPS del repartidor y las entregas realizadas.</p>
      </div>

      <Card className="p-4 flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-xs uppercase tracking-widest">Fecha</Label>
          <Input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} className="w-44" data-testid="mapa-fecha" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest">Repartidor</Label>
          <Select value={pilotoId} onValueChange={setPilotoId}>
            <SelectTrigger className="w-64" data-testid="mapa-piloto"><SelectValue placeholder="Selecciona…" /></SelectTrigger>
            <SelectContent>
              {repartidores.map((r) => <SelectItem key={r.id} value={r.id}>{r.name || r.email}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" onClick={load} disabled={loading}><RefreshCw className="w-4 h-4 mr-1" /> Actualizar</Button>
        <div className="w-full"><HistorialRutas pilotoId={pilotoId} fecha={fecha} onPick={setFecha} /></div>
        {data && <div className="w-full"><AutorizarInicio pilotoId={pilotoId} fecha={fecha} aut={data.autorizacion} onDone={load} /></div>}
      </Card>

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard label="Inicio" value={data.ruta?.iniciada_at ? new Date(data.ruta.iniciada_at).toLocaleTimeString("es-GT", { timeZone: "America/Guatemala" }) : "—"} icon={Play} />
            <StatCard label="Fin" value={data.ruta?.finalizada_at ? new Date(data.ruta.finalizada_at).toLocaleTimeString("es-GT", { timeZone: "America/Guatemala" }) : "—"} icon={StopCircle} />
            <StatCard label="KM recorridos" value={`${kms.toFixed(1)} km`} icon={RouteIcon} />
            <StatCard label="Entregas" value={String((data.entregas || []).length)} icon={MapPin} />
          </div>

          <Card className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xl font-semibold flex items-center gap-2"><MapPin className="w-4 h-4" /> Recorrido GPS</div>
              {mapUrl && (
                <a href={mapUrl} target="_blank" rel="noreferrer" className="bg-[#002FA7] hover:bg-[#001a5c] text-white text-xs font-black uppercase tracking-widest px-3 py-1.5 rounded inline-flex items-center gap-1">
                  Abrir en Google Maps <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
            {!inicio ? (
              <div className="text-center text-zinc-500 py-8 text-sm">Este repartidor aún no ha iniciado ruta en esta fecha.</div>
            ) : (
              <div className="text-xs text-zinc-600 space-y-1">
                <TrazoGPS pings={pings} />
                {data.ruta?.cierre_automatico && <div className="text-amber-700 font-bold">Ruta cerrada automáticamente a las 8:00 p.m.</div>}
                <div><b>Inicio:</b> {inicio.lat.toFixed(5)}, {inicio.lng.toFixed(5)} · {new Date(inicio.at).toLocaleTimeString("es-GT", { timeZone: "America/Guatemala" })}</div>
                {fin && fin !== inicio && <div><b>Fin:</b> {fin.lat.toFixed(5)}, {fin.lng.toFixed(5)} · {new Date(fin.at).toLocaleTimeString("es-GT", { timeZone: "America/Guatemala" })}</div>}
                <div><b>Total puntos GPS:</b> {pings.length}</div>
              </div>
            )}
          </Card>

          <Card className="p-4">
            <div className="text-xl font-semibold mb-3 flex items-center gap-2"><MapPin className="w-4 h-4" /> Entregas del día ({(data.entregas || []).length})</div>
            {(data.entregas || []).length === 0 ? (
              <div className="text-center text-zinc-500 py-6 text-sm">Sin entregas registradas.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-50 text-zinc-600 text-left text-[10px] uppercase tracking-widest">
                    <tr>
                      <th className="px-3 py-2">Hora</th>
                      <th className="px-3 py-2">Pedido</th>
                      <th className="px-3 py-2">Cliente</th>
                      <th className="px-3 py-2">Dirección</th>
                      <th className="px-3 py-2">Estado</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-100">
                    {(data.entregas || []).map((e) => (
                      <tr key={e.id}>
                        <td className="px-3 py-1.5 tabular-nums text-xs">{new Date(e.at).toLocaleTimeString("es-GT", { timeZone: "America/Guatemala" })}</td>
                        <td className="px-3 py-1.5">
                          <Link to={`/pedidos/${e.id}`} className="font-mono text-xs font-semibold text-[#002FA7] hover:underline inline-flex items-center gap-1">{e.numero} <ExternalLink className="w-3 h-3" /></Link>
                        </td>
                        <td className="px-3 py-1.5">{e.cliente}</td>
                        <td className="px-3 py-1.5 text-xs text-zinc-600">{e.direccion}</td>
                        <td className="px-3 py-1.5">
                          <span className={`text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded ${e.estado_entrega === "entregado" ? "bg-blue-600 text-white" : e.estado_entrega === "parcial" ? "bg-amber-500 text-white" : "bg-red-600 text-white"}`}>{e.estado_entrega}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <Card className="p-4" data-testid="registros-entrega">
            <div className="text-xl font-semibold mb-3 flex items-center gap-2"><MapPin className="w-4 h-4" /> Registro de entregas por escaneo ({(data.registros || []).length})</div>
            {(data.registros || []).length === 0 ? (
              <div className="text-center text-zinc-500 py-6 text-sm">Sin entregas escaneadas completas en esta fecha.</div>
            ) : (
              <div className="space-y-3">
                {data.registros.map((g) => (
                  <div key={g.id} className="border border-zinc-200 rounded p-3 text-sm" data-testid={`registro-${g.numero}`}>
                    <div className="flex flex-wrap justify-between gap-2">
                      <Link to={`/pedidos/${g.order_id}`} className="font-mono font-bold text-[#002FA7] hover:underline">{g.numero}</Link>
                      <span className="text-xs text-zinc-600">{new Date(g.at).toLocaleString("es-GT", { timeZone: "America/Guatemala" })} · {g.repartidor}</span>
                    </div>
                    <div className="font-semibold">{g.cliente}</div>
                    <div className="text-xs text-zinc-700 mt-1">{g.productos.map((p) => `${p.cantidad}× ${p.producto}`).join(" · ")}</div>
                    <div className="text-[11px] text-zinc-500 mt-1 font-mono break-all">QR: {g.codigos.map((c) => `${c.codigo} (${new Date(c.at).toLocaleTimeString("es-GT", { timeZone: "America/Guatemala", hour: "2-digit", minute: "2-digit" })})`).join(" · ")}</div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}

function AutorizarInicio({ pilotoId, fecha, aut, onDone }) {
  const [motivo, setMotivo] = useState("");
  if (fecha !== todayIso() || !pilotoId) return null;
  if (aut) return <div className="text-xs text-emerald-700 font-bold" data-testid="autorizacion-info">Inicio autorizado por {aut.por_nombre} · {aut.motivo}</div>;
  const go = async () => {
    try { await api.post("/domicilios/ruta/autorizar-inicio", { piloto_id: pilotoId, motivo }); toast.success("Inicio de ruta autorizado"); setMotivo(""); onDone(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="flex flex-wrap items-end gap-2" data-testid="autorizar-inicio">
      <div><Label className="text-xs uppercase tracking-widest">Autorizar inicio con productos faltantes</Label>
        <Input value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Motivo" className="w-64" data-testid="autorizar-motivo" /></div>
      <Button variant="outline" onClick={go} disabled={motivo.trim().length < 3} data-testid="autorizar-btn">Autorizar</Button>
    </div>
  );
}

function StatCard({ label, value, icon: Icon }) {
  return (
    <Card className="p-3">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-zinc-500 font-black"><Icon className="w-3 h-3" /> {label}</div>
      <div className="text-xl font-black mt-0.5">{value}</div>
    </Card>
  );
}
